import React from 'react';
import { TrendingUp, TrendingDown, Target, Zap, AlertCircle, CheckCircle } from 'lucide-react';
import { CalculatedMetrics } from '../../types';

interface QuickInsightsProps {
  data: CalculatedMetrics[];
  bias: 'long' | 'short';
}

interface Insight {
  icon: React.ElementType;
  title: string;
  description: string;
  value: string;
  trend: 'positive' | 'negative' | 'neutral';
  confidence: number;
  actionable?: string;
}

export const QuickInsights: React.FC<QuickInsightsProps> = ({ data, bias }) => {
  const generateInsights = (): Insight[] => {
    if (data.length === 0) {
      return [];
    }

    const insights: Insight[] = [];

    const redCloses = data.filter(row => row['Red Close'] === 1).length;
    const redCloseRate = (redCloses / data.length) * 100;
    const successRate = bias === 'long' ? (100 - redCloseRate) : redCloseRate;

    insights.push({
      icon: successRate >= 50 ? CheckCircle : AlertCircle,
      title: bias === 'long' ? 'Tasso di Successo Complessivo (Long)' : 'Tasso di Successo Complessivo (Short)',
      description: bias === 'long'
        ? (successRate >= 50
            ? 'La maggior parte dei gap mostra performance positive per strategie long'
            : 'La maggior parte dei gap chiude in rosso, considera strategie short')
        : (successRate >= 50
            ? 'La maggior parte dei gap chiude in rosso, ottimo per strategie short'
            : 'La maggior parte dei gap chiude in verde, attenzione alle strategie short'),
      value: `${successRate.toFixed(1)}%`,
      trend: successRate >= 50 ? 'positive' : 'negative',
      confidence: 95,
      actionable: bias === 'long'
        ? (successRate >= 50
            ? 'Favorisci posizioni long su setup selezionati'
            : 'Valuta strategie short o hedging')
        : (successRate >= 50
            ? 'Ottimo ambiente per short: entry su spike, target chiusure rosse'
            : 'Ambiente difficile per short: seleziona setup con cautela')
    });

    const spikes = data.map(row => (row['Spike %'] || 0) * 100).filter(s => isFinite(s));
    const avgSpike = spikes.reduce((sum, s) => sum + s, 0) / spikes.length;

    insights.push({
      icon: avgSpike >= 30 ? Zap : TrendingUp,
      title: 'Spike Medio',
      description: avgSpike >= 30
        ? 'Gli spike sono significativi, ottime opportunità di profitto rapido'
        : 'Spike moderati, pianifica target realistici',
      value: `${avgSpike.toFixed(1)}%`,
      trend: avgSpike >= 30 ? 'positive' : 'neutral',
      confidence: 90,
      actionable: avgSpike >= 30
        ? 'Sfrutta le prime ore per massimizzare profitti'
        : 'Considera swing trade invece di scalping'
    });

    const pmhBreaks = data.filter(row => row['HIGH > PMH'] === 'Y').length;
    const pmhBreakRate = (pmhBreaks / data.length) * 100;
    const pmhBreakIsPositive = bias === 'long' ? pmhBreakRate >= 50 : pmhBreakRate < 50;

    insights.push({
      icon: pmhBreakIsPositive ? Target : AlertCircle,
      title: 'Tasso di Break PMH',
      description: bias === 'long'
        ? (pmhBreakRate >= 50
            ? 'Alta probabilità di superare il premarket high'
            : 'Il premarket high spesso rappresenta resistenza')
        : (pmhBreakRate < 50
            ? 'PMH rappresenta forte resistenza, ottimo per short'
            : 'Attenzione: frequenti breakout del PMH, risk per short'),
      value: `${pmhBreakRate.toFixed(1)}%`,
      trend: pmhBreakIsPositive ? 'positive' : 'negative',
      confidence: 88,
      actionable: bias === 'long'
        ? (pmhBreakRate >= 50
            ? 'PMH può essere usato come target iniziale'
            : 'Considera PMH come zona di profit-taking')
        : (pmhBreakRate < 50
            ? 'Short da PMH con stop loss stretto sopra breakout'
            : 'Attendi conferma rejection PMH prima di entrare short')
    });

    const marketCaps = data.map(row => Number(row['Market Cap']) || 0).filter(mc => mc > 0);

    const formatMarketCap = (value: number): string => {
      if (value >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
      if (value >= 1e6) return `$${(value / 1e6).toFixed(0)}M`;
      if (value >= 1e3) return `$${(value / 1e3).toFixed(0)}K`;
      return `$${value.toFixed(0)}`;
    };

    const avgMarketCap = marketCaps.reduce((sum, mc) => sum + mc, 0) / marketCaps.length;

    const sortedMarketCaps = [...marketCaps].sort((a, b) => a - b);
    const medianMarketCap = sortedMarketCaps.length % 2 === 0
      ? (sortedMarketCaps[sortedMarketCaps.length / 2 - 1] + sortedMarketCaps[sortedMarketCaps.length / 2]) / 2
      : sortedMarketCaps[Math.floor(sortedMarketCaps.length / 2)];

    const marketCapStats = `${formatMarketCap(avgMarketCap)} - ${formatMarketCap(medianMarketCap)}`;

    insights.push({
      icon: TrendingUp,
      title: 'Capitalizzazione Media e Mediana',
      description: `Media e mediana di capitalizzazione del dataset analizzato`,
      value: marketCapStats,
      trend: 'neutral',
      confidence: 85,
      actionable: avgMarketCap < 50e6
        ? 'Maggiore volatilità richiede stop loss più ampi'
        : 'Liquidità migliore per esecuzioni precise'
    });

    const volumeRatios = data.map(row => row['Vol/PM Vol Ratio'] || 0).filter(v => isFinite(v));
    const avgVolumeRatio = volumeRatios.reduce((sum, v) => sum + v, 0) / volumeRatios.length;

    insights.push({
      icon: avgVolumeRatio >= 3 ? Zap : TrendingDown,
      title: 'Intensità Volume',
      description: avgVolumeRatio >= 3
        ? 'Volume in sessione molto superiore al premarket'
        : 'Volume moderato, attenzione alla liquidità',
      value: `${avgVolumeRatio.toFixed(1)}x`,
      trend: avgVolumeRatio >= 3 ? 'positive' : 'neutral',
      confidence: 80,
      actionable: avgVolumeRatio >= 3
        ? 'Ottima liquidità per entrate/uscite rapide'
        : 'Evita size troppo grandi su gap illiquidi'
    });

    const priceRanges = [
      { label: 'Sotto $2', data: data.filter(r => r.Open < 2) },
      { label: '$2-$5', data: data.filter(r => r.Open >= 2 && r.Open < 5) },
      { label: 'Sopra $5', data: data.filter(r => r.Open >= 5) }
    ];

    const bestPriceRange = priceRanges.reduce((best, current) => {
      if (current.data.length === 0) return best;
      const currentRedCloseRate = (current.data.filter(r => r['Red Close'] === 1).length / current.data.length) * 100;
      const currentSuccessRate = bias === 'long' ? (100 - currentRedCloseRate) : currentRedCloseRate;

      const bestRedCloseRate = best.data.length === 0 ? 0 : (best.data.filter(r => r['Red Close'] === 1).length / best.data.length) * 100;
      const bestSuccessRate = bias === 'long' ? (100 - bestRedCloseRate) : bestRedCloseRate;

      return currentSuccessRate > bestSuccessRate ? current : best;
    });

    if (bestPriceRange.data.length > 0) {
      const redCloseRate = (bestPriceRange.data.filter(r => r['Red Close'] === 1).length / bestPriceRange.data.length) * 100;
      const bestSuccessRate = bias === 'long' ? (100 - redCloseRate) : redCloseRate;

      insights.push({
        icon: Target,
        title: bias === 'long' ? 'Fascia di Prezzo Ottimale (Long)' : 'Fascia di Prezzo Ottimale (Short)',
        description: bias === 'long'
          ? `I gap nella fascia ${bestPriceRange.label} mostrano le migliori performance long`
          : `I gap nella fascia ${bestPriceRange.label} mostrano le migliori performance short`,
        value: bestPriceRange.label,
        trend: 'positive',
        confidence: 82,
        actionable: `Focus su gap in questa fascia (${bestSuccessRate.toFixed(1)}% successo ${bias === 'long' ? 'long' : 'short'})`
      });
    }

    return insights.slice(0, 6);
  };

  const insights = generateInsights();

  const getTrendColor = (trend: 'positive' | 'negative' | 'neutral') => {
    switch (trend) {
      case 'positive':
        return 'border-green-500/30 bg-green-500/5';
      case 'negative':
        return 'border-red-500/30 bg-red-500/5';
      default:
        return 'border-blue-500/30 bg-blue-500/5';
    }
  };

  const getTrendTextColor = (trend: 'positive' | 'negative' | 'neutral') => {
    switch (trend) {
      case 'positive':
        return 'text-green-400';
      case 'negative':
        return 'text-red-400';
      default:
        return 'text-blue-400';
    }
  };

  if (insights.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold text-white mb-2">Insight Chiave</h3>
        <p className="text-gray-400 text-sm">
          Analisi automatica dei pattern più rilevanti nel tuo dataset
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {insights.map((insight, index) => (
          <div
            key={index}
            className={`premium-chart-card border-2 ${getTrendColor(insight.trend)} hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className={`p-3 rounded-lg ${getTrendColor(insight.trend)}`}>
                <insight.icon className={`h-6 w-6 ${getTrendTextColor(insight.trend)}`} />
              </div>
              <div className="text-right">
                <span className="text-xs text-gray-400">Confidenza</span>
                <div className="flex items-center space-x-1 mt-1">
                  <div className="w-16 h-1.5 bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${getTrendTextColor(insight.trend).replace('text-', 'bg-')}`}
                      style={{ width: `${insight.confidence}%` }}
                    />
                  </div>
                  <span className="text-xs text-gray-300">{insight.confidence}%</span>
                </div>
              </div>
            </div>

            <h4 className="text-lg font-bold text-white mb-2">{insight.title}</h4>

            <div className={`text-3xl font-bold ${getTrendTextColor(insight.trend)} mb-3`}>
              {insight.value}
            </div>

            <p className="text-sm text-gray-300 mb-3 leading-relaxed">
              {insight.description}
            </p>

            {insight.actionable && (
              <div className={`mt-4 pt-4 border-t ${getTrendColor(insight.trend).split(' ')[0]}`}>
                <div className="flex items-start space-x-2">
                  <div className={`p-1 rounded ${getTrendColor(insight.trend)}`}>
                    <CheckCircle className={`h-3 w-3 ${getTrendTextColor(insight.trend)}`} />
                  </div>
                  <p className="text-xs text-gray-400 leading-relaxed">
                    <span className="font-semibold text-white">Azione:</span> {insight.actionable}
                  </p>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
